import { cn } from "@/lib/utils";
import type { AssistantConversationListItem } from "../../types";
import {
  describeConversation,
  describeConversationForScreenReader,
} from "./conversation-status-label";
import { ConversationStatusMark } from "./conversation-status-mark";
import { formatConversationTime } from "./conversation-time";

export type ConversationRowDensity = "default" | "dense";

/**
 * One conversation, drawn the same way on every surface.
 *
 * Two axes that never compete (the Pencil vocabulary):
 *
 * - **Status** is the mark. A working row gets an accent spinner in the left gutter; a
 *   needs-you row gets the blue dot at the right, beside the time. The gutter and the dot slot
 *   are reserved on every row so titles and times line up down the list whatever each row's
 *   status is.
 * - **Row state** is the treatment. Current is a 2px accent rule on the left edge plus a 600
 *   title and **no fill**; hover is a grey fill and nothing else — grey reads as backgrounded,
 *   so it must not be allowed to mean "current". `bg-muted` is that grey: `--muted` resolves
 *   to `oklch(0.967 0.001 286.375)` ≈ `#f4f4f5`, the token nearest the design's `#f5f5f5`
 *   (`.design-sync/conventions.md` names no literal hover grey).
 *
 * `density` picks the type scale: `default` is the flyout's 13/11, `dense` the panel's 12/11.
 */
export function ConversationRow({
  item,
  isCurrent,
  density = "default",
  onSelect,
  now,
}: {
  item: AssistantConversationListItem;
  isCurrent: boolean;
  density?: ConversationRowDensity;
  onSelect: (threadId: string) => void;
  /** Clock for the relative time; the list passes one so a whole list ticks together. */
  now?: number;
}) {
  const description = describeConversation(item);
  const isDense = density === "dense";
  const title = item.title.trim().length > 0 ? item.title.trim() : "Untitled conversation";

  return (
    <button
      type="button"
      aria-current={isCurrent ? "true" : undefined}
      aria-label={describeConversationForScreenReader(item)}
      onClick={() => onSelect(item.threadId)}
      // The row's identity and its status, published for the same reason the all-conversations
      // table publishes them: the status marks are `aria-hidden` glyphs and the title is the
      // only thing in the accessible name, so "which conversation is this, and what is it
      // doing" is otherwise unreadable from the DOM. Same spelling as the table's row, so one
      // vocabulary covers every surface.
      data-conversation-id={item.threadId}
      data-conversation-status={item.status}
      className={cn(
        "flex w-full items-start gap-2 border-b border-b-border border-l-2 text-left transition-colors hover:bg-muted",
        isDense ? "px-3 py-2" : "px-3 py-2.5",
        isCurrent ? "border-l-primary" : "border-l-transparent"
      )}
    >
      <span
        className={cn(
          "flex shrink-0 items-center justify-center",
          isDense ? "h-4 w-3.5" : "h-[18px] w-4"
        )}
      >
        <ConversationStatusMark
          status={item.status === "working" ? "working" : "idle"}
          size={density}
        />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            "truncate text-foreground",
            isDense ? "text-xs" : "text-[13px]",
            isCurrent ? "font-semibold" : "font-medium"
          )}
        >
          {title}
        </span>
        {description ? (
          <span
            className={cn(
              "truncate text-[11px]",
              description.tone === "accent" ? "text-primary" : "text-muted-foreground"
            )}
          >
            {description.text}
          </span>
        ) : null}
      </span>

      <span className="flex shrink-0 items-center gap-1.5 pt-px">
        <span className="text-[11px] text-neutral-dim tabular-nums">
          {formatConversationTime(item.lastActivityAt, now ?? Date.now())}
        </span>
        <span className="flex w-[7px] items-center justify-center">
          <ConversationStatusMark
            status={item.status === "needs_you" ? "needs_you" : "idle"}
            size={density}
          />
        </span>
      </span>
    </button>
  );
}
